export function pointerToken(value) {
    return String(value).replace(/~/g, "~0").replace(/\//g, "~1");
}

export function collectExpectedValues(value, pointer = "", result = []) {
    if (Array.isArray(value) || value === null || typeof value !== "object") {
        result.push({ pointer: pointer || "/", value });
        return result;
    }
    const entries = Object.entries(value);
    if (!entries.length) result.push({ pointer: pointer || "/", value });
    for (const [key, child] of entries) {
        collectExpectedValues(child, `${pointer}/${pointerToken(key)}`, result);
    }
    return result;
}

export function valueAtPointer(document, pointer) {
    if (pointer === "/") return { found: true, value: document };
    const tokens = pointer.slice(1).split("/").map((token) => token.replace(/~1/g, "/").replace(/~0/g, "~"));
    let value = document;
    for (const token of tokens) {
        if (value === null || typeof value !== "object" || !Object.prototype.hasOwnProperty.call(value, token)) {
            return { found: false, value: undefined };
        }
        value = value[token];
    }
    return { found: true, value };
}

export function managedConfigDifferences(expected, observed) {
    return collectExpectedValues(expected).flatMap(({ pointer, value }) => {
        const actual = valueAtPointer(observed, pointer);
        if (actual.found && JSON.stringify(actual.value) === JSON.stringify(value)) return [];
        return [{ pointer, expected: value, observed: actual.value, missing: !actual.found }];
    });
}

export function isPlainObject(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function managedConfigDocumentChanges(before, after, pointer = "", beforeFound = true, afterFound = true) {
    if (beforeFound && afterFound && JSON.stringify(before) === JSON.stringify(after)) return [];
    if (beforeFound && afterFound && isPlainObject(before) && isPlainObject(after)) {
        const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
        return [...keys].flatMap((key) => managedConfigDocumentChanges(
            before[key],
            after[key],
            `${pointer}/${pointerToken(key)}`,
            Object.prototype.hasOwnProperty.call(before, key),
            Object.prototype.hasOwnProperty.call(after, key),
        ));
    }
    return [{ pointer: pointer || "/", before, after, beforeFound, afterFound }];
}

export function pointerTokens(pointer) {
    if (pointer === "/") return [];
    return pointer.slice(1).split("/").map((token) => token.replace(/~1/g, "/").replace(/~0/g, "~"));
}

export function applyDocumentChange(document, change) {
    if (change.pointer === "/") return change.afterFound ? structuredClone(change.after) : {};
    const result = document;
    const tokens = pointerTokens(change.pointer);
    let parent = result;
    for (const token of tokens.slice(0, -1)) {
        if (!isPlainObject(parent[token])) parent[token] = {};
        parent = parent[token];
    }
    const key = tokens[tokens.length - 1];
    if (change.afterFound) parent[key] = structuredClone(change.after);
    else delete parent[key];
    return result;
}

export function pointersOverlap(a, b) {
    const left = pointerTokens(a);
    const right = pointerTokens(b);
    const shared = Math.min(left.length, right.length);
    return left.slice(0, shared).every((token, index) => token === right[index]);
}

export function mergeManagedConfigDocuments(base, draft, landed) {
    const draftChanges = managedConfigDocumentChanges(base, draft);
    const landedChanges = managedConfigDocumentChanges(base, landed);
    const conflicts = draftChanges.filter((draftChange) => landedChanges.some((landedChange) => (
        pointersOverlap(draftChange.pointer, landedChange.pointer)
        && !(draftChange.afterFound === landedChange.afterFound
            && JSON.stringify(draftChange.after) === JSON.stringify(landedChange.after))
    ))).map((change) => change.pointer);
    let merged = structuredClone(landed);
    for (const change of draftChanges) merged = applyDocumentChange(merged, change);
    return { merged, conflicts };
}

export function displayConfigValue(value) {
    if (value === undefined) return "not present";
    const rendered = JSON.stringify(value);
    return rendered === undefined ? String(value) : rendered;
}
